/**
 * COMPLIANCE SERVICE - API ABSTRACTION LAYER
 * 
 * This file provides a single import point for all Compliance API operations.
 * MSW intercepts requests when demo mode is active — no in-code toggle needed.
 * 
 * USAGE IN COMPONENTS:
 * import { complianceService } from '@/app/services/api/compliance/complianceService';
 * 
 * const data = await complianceService.dashboard.getMetrics();
 */

import * as complianceClient from './complianceClient';

// ==============================================
// SERVICE SELECTION - Always use real client
// MSW handles mocking at the network level
// ==============================================
const service = complianceClient;

// ==============================================
// EXPORTED SERVICE INTERFACE
// ==============================================
export const complianceService = {
  // Stores and Regions
  auditors: {
    getAll: service.getAllAuditors,
  },

  // Compliance Reference Controller (new /compliance/stores & /compliance/regions)
  complianceStores: {
    getAll: service.getComplianceStores,
    getById: service.getComplianceStoreById,
    getByNumber: service.getComplianceStoreByNumber,
  },

  complianceRegions: {
    getAll: service.getComplianceRegions,
  },

  // Dashboard
  dashboard: {
    getMetrics: service.getDashboardMetrics,
    getAuditScoreTrends: service.getAuditScoreTrends,
  },

  // Audit Types
  auditTypes: {
    getAll: service.getAllAuditTypes,
    getById: service.getAuditTypeById,
    create: service.createAuditType,
    update: service.updateAuditType,
    delete: service.deleteAuditType,
  },

  // Audit Templates
  auditTemplates: {
    getAll: service.getAllAuditTemplates,
    getById: service.getAuditTemplateById,
    create: service.createAuditTemplate,
    update: service.updateAuditTemplate,
    delete: service.deleteAuditTemplate,
    publish: service.publishAuditTemplate,
  },

  // Audits
  audits: {
    getAll: service.getAllAudits,
    getById: service.getAuditById,
    create: service.createAudit,
    update: service.updateAudit,
    submit: service.submitAudit,
    approve: service.approveAudit,
    challenge: service.challengeAudit,
    delete: service.deleteAudit,
    updateResponses: service.updateAuditResponses,
    uploadPhoto: service.uploadAuditPhoto,
    downloadPdf: service.downloadAuditPdf,
    previewPdf: service.previewAuditPdf,
  },

  // Audit Schedules
  auditSchedules: {
    getAll: service.getAllAuditSchedules,
    getById: service.getAuditScheduleById,
    create: service.createAuditSchedule,
    update: service.updateAuditSchedule,
    delete: service.deleteAuditSchedule,
    trigger: service.triggerAuditSchedule,
  },
  // Action Items
  actionItems: {
    getAll: service.getAllActionItems,
    getById: service.getActionItemById,
    create: service.createActionItem,
    update: service.updateActionItem,
    resolve: service.resolveActionItem,
    close: service.closeActionItem,
    addFeedback: service.addActionItemFeedback,
    delete: service.deleteActionItem,
  },

  // Job Roles
  jobRoles: {
    getAll: service.getAllJobRoles,
    getById: service.getJobRoleById,
    create: service.createJobRole,
    update: service.updateJobRole,
    delete: service.deleteJobRole,
  },

  // Notifications
  notifications: {
    getByUserId: service.getNotificationsByUserId,
    markAsRead: service.markNotificationAsRead,
    markAllAsRead: service.markAllNotificationsAsRead,
  },

  // Audit Reports
  reports: {
    auditSummary: {
      generate: service.generateAuditSummaryReport,
      preview: service.previewAuditSummaryReport,
      export: service.exportAuditSummaryReport,
    },
    auditDetails: {
      generate: service.generateAuditDetailReport,
      preview: service.previewAuditDetailReport,
      export: service.exportAuditDetailReport,
    },
    actionItems: {
      generate: service.generateActionItemsSummaryReport,
      preview: service.previewActionItemsSummaryReport,
      export: service.exportActionItemsSummaryReport,
    },
    complianceOverview: {
      generate: service.generateComplianceOverviewReport,
      preview: service.previewComplianceOverviewReport,
      export: service.exportComplianceOverviewReport,
    },
  },
};

// MSW handles mocking at the network level now
export const isUsingMockData = false;

// Export type for the service (useful for TypeScript)
export type ComplianceService = typeof complianceService;
